import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  Loader2,
  AtSign
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_NUMBER = "(940) 337-9761";
const PHONE_LINK = "tel:+19403379761";
const ADDRESS = "109 Henrietta St, Wichita Falls, TX 76301";
const EMAIL = "jantony80@gmail.com";
const EMAIL_LINK = "mailto:jantony80@gmail.com";
const MAPS_EMBED_URL = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3314.859073!2d-98.4932!3d33.9137!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDU0JzQ5LjMiTiA5OMKwMjknMzUuNSJX!5e0!3m2!1sen!2sus!4v1234567890";

const businessHours = [
  { day: "Monday", hours: "9:30 AM - 5:00 PM" },
  { day: "Tuesday", hours: "9:30 AM - 5:00 PM" },
  { day: "Wednesday", hours: "9:30 AM - 5:00 PM" },
  { day: "Thursday", hours: "9:30 AM - 5:00 PM" },
  { day: "Friday", hours: "9:30 AM - 5:00 PM" },
  { day: "Saturday", hours: "11:00 AM - 1:00 PM" },
  { day: "Sunday", hours: "Closed" },
];

const serviceAreas = [
  "Wichita Falls",
  "Burkburnett",
  "Iowa Park",
  "Henrietta",
  "Electra",
  "Vernon",
];

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", phone: "", email: "", message: "" });
      setIsSent(true);
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight text-foreground mb-6 animate-fade-in-up">
              Contact <span className="text-primary">Us</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-in-up delay-100">
              Ready to get your vehicle serviced? Give us a call or send us a message. 
              We're here to help!
            </p>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8 animate-fade-in-up">
              {/* Phone Card */}
              <Card className="bg-primary border-0">
                <CardContent className="p-6">
                  <a
                    href={PHONE_LINK}
                    data-testid="contact-phone-btn"
                    className="flex items-center gap-4"
                  >
                    <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                      <Phone className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="text-blue-100 text-sm">Call Us Now</p>
                      <p className="text-white font-heading text-2xl md:text-3xl">{PHONE_NUMBER}</p>
                    </div>
                  </a>
                </CardContent>
              </Card>

              {/* Address */}
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl uppercase text-foreground mb-2">Location</h3>
                      <p className="text-muted-foreground">{ADDRESS}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email */}
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <a
                    href={EMAIL_LINK}
                    data-testid="contact-email-btn"
                    className="flex items-start gap-4"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <AtSign className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl uppercase text-foreground mb-2">Email Us</h3>
                      <p className="text-primary hover:text-blue-400 transition-colors">{EMAIL}</p>
                    </div>
                  </a>
                </CardContent>
              </Card>

              {/* Business Hours */}
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl uppercase text-foreground mb-4">Business Hours</h3>
                      <div className="space-y-2">
                        {businessHours.map((item) => (
                          <div key={item.day} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{item.day}</span>
                            <span className={item.hours === "Closed" ? "text-red-400" : "text-foreground"}>
                              {item.hours}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Service Areas */}
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl uppercase text-foreground mb-4">Service Areas</h3>
                  <div className="flex flex-wrap gap-2">
                    {serviceAreas.map((area) => (
                      <span
                        key={area}
                        className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-sm"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="animate-fade-in-up delay-200">
              <Card className="bg-card border-border">
                <CardContent className="p-6 md:p-8">
                  <h2 className="font-heading text-2xl uppercase text-foreground mb-6">
                    Send Us a <span className="text-primary">Message</span>
                  </h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-foreground">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-name-input"
                        className="bg-muted/50 border-border focus:border-primary h-12"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-foreground">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="Your phone number"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="contact-phone-input"
                        className="bg-muted/50 border-border focus:border-primary h-12"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-foreground">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Your email address"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-email-input"
                        className="bg-muted/50 border-border focus:border-primary h-12"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-foreground">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="How can we help you?"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        data-testid="contact-message-input"
                        className="bg-muted/50 border-border focus:border-primary resize-none"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                      className="cta-primary w-full flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </Button>

                    {isSent && (
                      <p
                        data-testid="contact-success-message"
                        className="text-sm text-green-400 text-center"
                      >
                        Message sent! We'll get back to you soon.
                      </p>
                    )}

                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section data-testid="map-section" className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-foreground mb-4">
              Find <span className="text-primary">Us</span>
            </h2>
            <p className="text-muted-foreground">{ADDRESS}</p>
          </div>
          
          <div className="map-container">
            <iframe
              src={MAPS_EMBED_URL}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Tony's Auto Quality Service Location"
              className="map-dark"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
